import React, { FC, useRef, useState, useEffect } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';

import { ROUTE_LIST_DETAIL } from 'routes';
import { CardSchema, UnionExtraData } from 'constants/graphqlTypes';

import { TEST_ACTION_THOUGHT } from 'constants/aqa/pickDetails';
import { getWebDate, getWeeklyDateFormat } from 'helpers/getDateFormat';
import { useReactionContext } from 'components/Reactions/hooks';
import withHapticFeedback from 'helpers/withHapticFeedback';
import useThoughtsModal from 'helpers/useThoughtsModal';
import usePageType from 'helpers/usePageType';

import UILink from 'components/UI/Link';
import ThoughtLastUpdate from 'components/Thoughts/Thought/ThoughtLastUpdate';

import { StyledAction, StyledText, StyledTruncateWrapper } from './styled';

interface IActionContentProps {
  item: CardSchema;
  action: UnionExtraData;
  isDetail?: boolean;
  isSmall?: boolean;
  isVirtualAction?: boolean;
}

export const ACTION_WRAPPER_TRUNCATE_CLASS = 'action-truncate-wrapper';

const CHARACTER_LIMIT = 80; // Adjust as needed for your layout

const ActionContent: FC<IActionContentProps> = ({ action, item, isDetail, isVirtualAction }) => {
  const { id, responseToQuestion, questionGuestList } = item;
  const { isItemDetail } = usePageType();
  const [displayedText, setDisplayedText] = useState('');

  const user = action?.user;
  const { username = '' } = user ?? {};

  const openThoughts = useThoughtsModal({ id, hideTabs: !isItemDetail, openFromCardDetailPage: isItemDetail });

  const { isQuickReactionOpen } = useReactionContext();

  const handleTruncateClick = () => {
    if (!isVirtualAction && !isQuickReactionOpen) {
      withHapticFeedback(openThoughts)();
    }
  };

  const cardRef = useRef<HTMLDivElement>(null);
  const thoughtTextRef = useRef<HTMLDivElement>(null);
  const [shouldAnimateText, setShouldAnimateText] = useState(false);

  useEffect(() => {
    if (!isDetail && action?.__typename === 'CardCommentSchema' && action.text && shouldAnimateText) {
      let currentIndex = 0;
      const text = action.text.length > CHARACTER_LIMIT ? action.text.slice(0, CHARACTER_LIMIT) : action.text;
      const needsEllipsis = action.text.length > CHARACTER_LIMIT;
      const textWithQuotes = `"${text}${needsEllipsis ? '...' : '"'}`;
      setDisplayedText('');
      const typewriterInterval = setInterval(() => {
        if (currentIndex < textWithQuotes.length) {
          setDisplayedText(textWithQuotes.slice(0, currentIndex + 1));
          currentIndex += 1;
        } else {
          clearInterval(typewriterInterval);
        }
        return null;
      }, 20);
      return () => clearInterval(typewriterInterval);
    }
    return undefined;
  }, [action, isDetail, shouldAnimateText]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldAnimateText(true);
          observer.disconnect();
        }
        // Do NOT set to false on exit
        return null;
      },
      {
        rootMargin: '-112px 0px 0px 0px',
        threshold: 0.1,
      }
    );
    if (thoughtTextRef.current) observer.observe(thoughtTextRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={cardRef}>
      {action?.__typename === 'CardCommentSchema' && (!action?.isSuspicious || isDetail) && (
        <>
          <StyledTruncateWrapper className={ACTION_WRAPPER_TRUNCATE_CLASS}>
            {isDetail ? (
              action?.text && (
                <StyledText data-testid={TEST_ACTION_THOUGHT} onClickCapture={handleTruncateClick}>
                  &ldquo;{action?.text}&rdquo;
                </StyledText>
              )
            ) : (
              <div ref={thoughtTextRef} style={{ display: 'block' }}>
                <StyledText data-testid={TEST_ACTION_THOUGHT} onClickCapture={handleTruncateClick}>
                  {displayedText}
                </StyledText>
              </div>
            )}
          </StyledTruncateWrapper>
          {isItemDetail && (
            <ThoughtLastUpdate
              lastUpdate={getWeeklyDateFormat(action.createdAt)}
              username={username}
              parentCard={action?.collection}
              guestListQuestion={questionGuestList}
              responseToQuestion={responseToQuestion}
            />
          )}
        </>
      )}
      {(action?.__typename === 'CardPickSchema' ||
        (action?.__typename === 'CardCommentSchema' && action?.isSuspicious && !isDetail)) && (
        <StyledAction>
          Added &nbsp;
          <b>
            {formatDistanceToNowStrict(getWebDate(action?.createdAt), {
              addSuffix: true,
              roundingMethod: 'floor',
            })}
          </b>
        </StyledAction>
      )}
      {action?.__typename === 'CollectionCardLinkSchema' && (
        <StyledAction>
          Added to&nbsp;
          <UILink route={ROUTE_LIST_DETAIL} params={{ listId: action?.collection?.id }}>
            <b>{action?.collection?.title}</b>
          </UILink>
        </StyledAction>
      )}
    </div>
  );
};

export default ActionContent;
